/**
 * Boots the editor: check whether a password is needed, load the page, then render.
 *
 * The password screen is here rather than on the server because the shell is a static file —
 * the gate that matters is on `/api`, which refuses without a session whatever the browser
 * chooses to render.
 */

import { useEffect, useState } from 'react';
import { EditorHost } from './editor/EditorHost.js';
import { useDocStore } from './store/docStore.js';
import { api, ApiError } from './api/client.js';
import { BrandLockup } from './brand.js';

type Gate = 'checking' | 'locked' | 'open';

export function App(): React.ReactElement {
  const [gate, setGate] = useState<Gate>('checking');
  const load = useDocStore((s) => s.load);
  const ready = useDocStore((s) => s.ready);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        setGate(!me.required || me.authed ? 'open' : 'locked');
      } catch {
        // No server, or it is unreachable. The editor still works against the local cache,
        // so unlocking here is right — the API would refuse anything sensitive anyway.
        if (!cancelled) setGate('open');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (gate === 'open') void load();
  }, [gate, load]);

  if (gate === 'checking') return <Splash label="Starting…" />;
  if (gate === 'locked') return <PasswordGate onUnlocked={() => setGate('open')} />;
  if (!ready) return <Splash label="Loading your page…" />;
  return <EditorHost />;
}

function Splash({ label }: { label: string }): React.ReactElement {
  return (
    <div className="lp-splash">
      <BrandLockup />
      <p>{label}</p>
    </div>
  );
}

function PasswordGate({ onUnlocked }: { onUnlocked: () => void }): React.ReactElement {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      onUnlocked();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'That password is not right.'
          : 'Could not reach the server.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="lp-splash">
      <BrandLockup />
      <form className="lp-gate" onSubmit={submit}>
        <label className="lps__label" htmlFor="lp-password">
          Team password
        </label>
        <input
          id="lp-password"
          className="lpf__input"
          type="password"
          autoFocus
          autoComplete="current-password"
          value={password}
          disabled={busy}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && <span className="lpf__error">{error}</span>}
        <button type="submit" className="lp-btn lp-btn--primary" disabled={busy || !password}>
          {busy ? 'Checking…' : 'Open the builder'}
        </button>
      </form>
    </div>
  );
}
